import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * PersonalLink — resolve o link curto /nome/cpf3 do usuário/afiliado.
 * Redireciona automaticamente para:
 *  - Funil do produtor com ?ref=affiliate_code (afiliação)
 *  - /member/login se for membro sem funil vinculado
 *  - /login como fallback
 */
export default function PersonalLink() {
  const { name, suffix } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/link/${encodeURIComponent(name)}/${encodeURIComponent(suffix)}`);
        if (!alive) return;
        setState({ loading: false, error: null, data });
        // Auto-redirect após ~800ms para dar tempo do usuário ver a mensagem
        const target = data.redirect_to;
        if (target) {
          setTimeout(() => nav(target, { replace: true }), 800);
        }
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, error: e?.response?.data?.detail || "Link não encontrado", data: null });
      }
    })();
    return () => { alive = false; };
  }, [name, suffix, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="personal-link-page">
      <div className="max-w-md w-full text-center">
        {state.loading && (
          <div className="flex flex-col items-center gap-3 opacity-80">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--accent)]" />
            <p className="text-sm">Carregando…</p>
          </div>
        )}
        {state.error && (
          <div className="border border-red-500/30 bg-red-500/5 rounded-sm p-6">
            <h1 className="text-xl font-bold text-red-400 mb-2">Link inválido</h1>
            <p className="text-sm opacity-70">{state.error}</p>
            <a href="/" className="mt-4 inline-block text-sm text-[var(--accent)] underline">Ir para a página inicial</a>
          </div>
        )}
        {state.data && (
          <div className="border border-[var(--border)] rounded-sm p-6">
            <p className="text-xs opacity-60 uppercase tracking-wider">
              {state.data.type === "user" ? "Indicado por" : "Área de acesso"}
            </p>
            <h1 className="text-2xl font-black mt-1">{state.data.name}</h1>
            {state.data.funnel_title && (
              <p className="mt-3 text-sm opacity-80">Redirecionando para <b>{state.data.funnel_title}</b>…</p>
            )}
            {!state.data.funnel_title && state.data.type === "member" && (
              <p className="mt-3 text-sm opacity-80">Redirecionando para sua área de aluno…</p>
            )}
            <Loader2 className="w-5 h-5 animate-spin text-[var(--accent)] mx-auto mt-4" />
          </div>
        )}
      </div>
    </div>
  );
}
